// Aba CONTRATO (Editar Usuário) — GPS Drive Backend próprio (2026-08-28),
// primeiro backend real desta aba. Vinculado ao userId interno (UUID),
// mesmo id que GET/PUT /users/:userId/client-profile já usa.
import { gpsDriveDelete, gpsDriveGet, gpsDrivePost, gpsDrivePut } from "@/lib/api/gpsdrive-client";

// Veículos gravados no próprio contrato (menu Vendas, 2026-10-05) — o
// contrato nunca precisa de rastreador vinculado. Vazio = documento usa os
// dispositivos do cliente (comportamento antigo).
export interface ContractVehicle {
  plate: string;
  model: string;
  year: string;
  color: string;
  plan: string;
  /** Mensalidade deste veículo (2026-10-15). */
  monthlyValue?: number | null;
}

export interface ClientContract {
  templateId: string | null;
  signatoryId: string | null;
  // Vários sócios/testemunhas (2026-10-14); signatoryId = o primeiro.
  signatoryIds: string[];
  contractValue: number | null;
  recurrenceValue: number | null;
  // Preenchido pelo botão "Marcar como assinado" OU pelo cliente concluindo
  // o link de autoatendimento (ver signedMethod).
  signedAt: string | null;
  // "manual" (botão "Marcar como assinado") ou "link" (autoatendimento via
  // /contrato/assinar/:token) — decide se a aba CONTRATO mostra o bloco de
  // prova (nome/geolocalização/foto) ou só a data.
  signedMethod: "manual" | "link" | null;
  signingToken: string | null;
  signedByName: string | null;
  signedLatitude: number | null;
  signedLongitude: number | null;
  signedPhotoKey: string | null;
  // Link assinado com validade (pasta de selfies não é pública, segurança
  // 2026-09-24) — sempre usar este, nunca montar a URL a partir da chave.
  signedPhotoUrl: string | null;
  // Certificado de assinaturas (2026-10-14): servidor NTP.br que deu a hora
  // de signedAt ("servidor" = nenhum respondeu), IP/navegador de quem
  // assinou e código curto do documento.
  signedTimeSource?: string | null;
  signedIp?: string | null;
  signedUserAgent?: string | null;
  documentCode?: string;
  vehicles: ContractVehicle[];
  /** Versão do contrato — "Novo contrato" depois de assinado soma 1 (2026-10-15). */
  version?: number;
}

/** Contrato assinado anterior, guardado ao gerar um novo (2026-10-15). */
export interface ClientContractVersion {
  id: string;
  version: number;
  signedAt: string | null;
  archivedAt: string;
  contract: ClientContract;
  template: { title: string; content: string } | null;
}

export type UpdateClientContractInput = Pick<
  ClientContract,
  "templateId" | "signatoryId" | "contractValue" | "recurrenceValue"
> & {
  // Ausente = mantém os veículos já salvos (aba CONTRATO não manda).
  vehicles?: ContractVehicle[];
  // Quando vem, manda sobre signatoryId.
  signatoryIds?: string[];
};

export const gpsDriveClientContractService = {
  // null se a aba CONTRATO deste usuário nunca foi salva ainda.
  get: (userId: string) => gpsDriveGet<ClientContract | null>(`/api/v1/users/${userId}/contract`),
  replace: (userId: string, input: UpdateClientContractInput) =>
    gpsDrivePut<ClientContract>(`/api/v1/users/${userId}/contract`, input),
  // Marca como assinado e envia a Mensagem de Boas-Vindas por WhatsApp —
  // ver client-contracts.controller.ts#POST /:userId/contract/sign no
  // backend. Erros comuns: telefone do cliente não cadastrado, Mensagem
  // de Boas-Vindas não configurada, WhatsApp não conectado (Integrações).
  sign: (userId: string) => gpsDrivePost<ClientContract>(`/api/v1/users/${userId}/contract/sign`, {}),
  // Botão "Gerar link de assinatura" — token único pra
  // /contrato/assinar/:token (autoatendimento do cliente, sem login).
  generateSigningLink: (userId: string) =>
    gpsDrivePost<{ token: string }>(`/api/v1/users/${userId}/contract/signing-link`, {}),
  // Botão "Enviar por WhatsApp" — só disponível quando a integração está
  // conectada (ver useWhatsappIntegration). `url` já vem pronta (com
  // origem) do frontend, o backend não conhece o domínio.
  sendSigningLinkByWhatsapp: (userId: string, url: string) =>
    gpsDrivePost<{ sent: boolean }>(`/api/v1/users/${userId}/contract/signing-link/send-whatsapp`, { url }),
  // "Novo contrato": guarda o assinado e reabre como versão +1 (mesmos dados).
  startNewVersion: (userId: string) => gpsDrivePost<ClientContract>(`/api/v1/users/${userId}/contract/new-version`, {}),
  versions: (userId: string) => gpsDriveGet<ClientContractVersion[]>(`/api/v1/users/${userId}/contract/versions`),
  // Excluir o atual (só administrador desde 2026-10-23, inclusive assinado).
  // Devolve o anterior assinado, que volta a ser o atual, ou null se o
  // cliente ficou sem contrato.
  remove: (userId: string) => gpsDriveDelete<ClientContract | null>(`/api/v1/users/${userId}/contract`),
  // Excluir um contrato anterior (só administrador).
  removeVersion: (userId: string, versionId: string) =>
    gpsDriveDelete<void>(`/api/v1/users/${userId}/contract/versions/${versionId}`),
};
